import React, { useState } from 'react';
import { XIcon, ChevronDownIcon, ChevronUpIcon, Pill, BriefcaseIcon } from 'lucide-react';
import { DiscountInput } from './DiscountInput';
import type { PaymentLineItemFormData, DiscountType } from '../../types/services';
import { calculateLineItemNetAmount, formatCurrency } from '../../types/services';

interface LineItemRowProps {
  item: PaymentLineItemFormData;
  onUpdate: (updates: Partial<PaymentLineItemFormData>) => void;
  onRemove: () => void;
  index: number;
}

export function LineItemRow({ item, onUpdate, onRemove, index }: LineItemRowProps) {
  const [showDiscount, setShowDiscount] = useState(
    Boolean(item.discount_type && item.discount_value)
  );

  const { grossAmount, discountAmount, netAmount } = calculateLineItemNetAmount(
    item.quantity,
    item.unit_price,
    item.discount_type,
    item.discount_value
  );

  const getItemIcon = () => {
    if (item.item_type === 'supplement') {
      return (
        <div className="w-8 h-8 rounded-full bg-[#5B8E87]/10 flex items-center justify-center">
          <Pill className="w-4 h-4 text-[#5B8E87]" />
        </div>
      );
    }
    if (item.item_type === 'service') {
      return (
        <div className="w-8 h-8 rounded-full bg-[#C9A870]/10 flex items-center justify-center">
          <BriefcaseIcon className="w-4 h-4 text-[#C9A870]" />
        </div>
      );
    }
    return (
      <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
        <span className="text-sm font-medium text-gray-600">{index + 1}</span>
      </div>
    );
  };

  return (
    <div className="bg-gray-50 rounded-xl p-4 space-y-3">
      {/* Header row with remove button */}
      <div className="flex items-start gap-3">
        {getItemIcon()}
        <div className="flex-1 min-w-0">
          <input
            type="text"
            value={item.description}
            onChange={(e) => onUpdate({ description: e.target.value })}
            placeholder="Item description"
            className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all text-sm font-medium"
          />
        </div>
        <button
          onClick={onRemove}
          className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
        >
          <XIcon className="w-4 h-4" />
        </button>
      </div>

      {/* Quantity and price row */}
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Qty</label>
          <input
            type="number"
            value={item.quantity}
            onChange={(e) =>
              onUpdate({ quantity: Math.max(1, parseInt(e.target.value) || 1) })
            }
            min="1"
            className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Rate (₹)</label>
          <input
            type="number"
            value={item.unit_price}
            onChange={(e) =>
              onUpdate({ unit_price: Math.max(0, parseFloat(e.target.value) || 0) })
            }
            min="0"
            step="1"
            className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Total</label>
          <div className="px-3 py-2 bg-white rounded-lg border border-gray-200 text-sm font-medium text-gray-900">
            {formatCurrency(grossAmount)}
          </div>
        </div>
      </div>

      {/* Discount toggle and input */}
      <div>
        <button
          type="button"
          onClick={() => {
            if (showDiscount) {
              // Clear discount when hiding
              onUpdate({
                discount_type: undefined,
                discount_value: undefined,
                discount_reason: undefined,
              });
            }
            setShowDiscount(!showDiscount);
          }}
          className="flex items-center gap-1 text-sm text-[#5B8E87] hover:text-[#4F7A74] transition-colors"
        >
          {showDiscount ? (
            <ChevronUpIcon className="w-4 h-4" />
          ) : (
            <ChevronDownIcon className="w-4 h-4" />
          )}
          {showDiscount ? 'Hide discount' : 'Add discount'}
        </button>

        {showDiscount && (
          <div className="mt-2 p-3 bg-white rounded-lg border border-gray-200">
            <DiscountInput
              discountType={item.discount_type}
              discountValue={item.discount_value}
              discountReason={item.discount_reason}
              maxAmount={grossAmount}
              onDiscountTypeChange={(type) =>
                onUpdate({ discount_type: type ?? undefined })
              }
              onDiscountValueChange={(value) => onUpdate({ discount_value: value })}
              onDiscountReasonChange={(reason) =>
                onUpdate({ discount_reason: reason })
              }
              showReason
            />
          </div>
        )}
      </div>

      {/* Net amount if discount applied */}
      {discountAmount > 0 && (
        <div className="flex items-center justify-between pt-2 border-t border-gray-200">
          <div className="text-sm text-gray-500">
            <span className="text-red-500">-{formatCurrency(discountAmount)}</span>
            {item.discount_reason && (
              <span className="text-gray-400 ml-2">({item.discount_reason})</span>
            )}
          </div>
          <div className="text-sm font-semibold text-gray-900">
            Net: {formatCurrency(netAmount)}
          </div>
        </div>
      )}
    </div>
  );
}
